import { useContext, useEffect, useMemo, useState } from 'react';
import clienteAxios from '../../config/axios';
import { useT } from 'i18n';
import SaldosContext from '../../context/saldos/SaldosContext';
import { avance, diasHasta, money, num, textoDias } from './utils';

const MINIMO = 50; // el servidor exige como mínimo 50 USDT
const ATAJOS = [50, 100, 250, 500];

const PanelOperacion = ({ p: proyecto, secundario, onCerrar, onHecho }) => {
  const { t, ts } = useT();
  const [p, setP] = useState(proyecto);
  const [parcelas, setParcelas] = useState([]);
  const [docs, setDocs] = useState([]);
  const { saldos, consultarApiSaldo } = useContext(SaldosContext);
  const [monto, setMonto] = useState(String(MINIMO));
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  const disponibleWallet = num(saldos?.DISPONIBLE);
  const dias = diasHasta(p.end_date);
  const restante = Math.max(0, num(p.amount) - num(p.amount_investment));
  const pct = avance(p);

  useEffect(() => { setP(proyecto); }, [proyecto]);

  useEffect(() => {
    let vivo = true;
    if (!proyecto || !proyecto.nrocontrato) return undefined;
    clienteAxios.post('/api/client/projects/asociadas', { nrocontrato: `${proyecto.nrocontrato}` })
      .then((r) => { if (vivo) setParcelas(r.data.data || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [proyecto]);

  useEffect(() => {
    let vivo = true;
    setDocs([]);
    if (!p || !p.id) return undefined;
    clienteAxios.post(`/api/client/projects/documents/${p.id}`)
      .then((r) => { if (vivo) setDocs(r.data.data || []); })
      .catch(() => {});
    return () => { vivo = false; };
  }, [p && p.id]); // eslint-disable-line

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onCerrar(); };
    document.addEventListener('keydown', esc);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', esc); document.body.style.overflow = ''; };
  }, [onCerrar]);

  const importe = secundario ? num(p.precioventa) : num(monto);
  const estimado = useMemo(() => {
    const periodo = num(p.tax_referential_client) / 360 * (dias ?? 0); // % sobre el período
    return importe * (1 + periodo / 100);
  }, [importe, p.tax_referential_client, dias]);

  const falta = importe > disponibleWallet;

  const confirmar = async () => {
    setError('');
    if (!secundario) {
      if (!(importe >= MINIMO)) { setError(t('panel.errMinimo', { min: MINIMO })); return; }
      if (importe > restante && restante > 0) { setError(t('panel.errRestante', { monto: money(restante) })); return; }
    }
    if (falta) { setError(t('panel.errSaldo')); return; }
    setEnviando(true);
    try {
      const r = secundario
        ? await clienteAxios.post('/api/client/investments/create_secundary', { offer_id: p.offer_id })
        : await clienteAxios.post('/api/client/investments/create', { amount: importe, project_id: p.id });
      setExito(ts(r.data.message) || t('panel.exitoDef'));
      if (consultarApiSaldo) consultarApiSaldo();
      if (onHecho) onHecho();
    } catch (e) {
      setError(ts(e.response?.data?.message) || t('panel.errorDef'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="p7m-overlay" onClick={onCerrar}>
      <aside className="p7m-panel" role="dialog" aria-modal="true" aria-label={p.name} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="p7m-cerrar" onClick={onCerrar} aria-label={t('panel.cerrar')}>×</button>

        <span className="p7m-chip">{p.category || t('mercado.proyecto')}</span>
        <h2>{p.name}</h2>
        <p className="p7m-card-sub">{[p.oferente?.name || p.issuer, p.nrocontrato && t('panel.contrato', { n: p.nrocontrato })].filter(Boolean).join(' · ')}</p>

        {p.description && <p className="p7m-desc">{p.description}</p>}

        <section className="p7m-dist" aria-label={t('panel.distribucion')}>
          <svg viewBox="0 0 42 42" className="p7m-dona" role="img" aria-label={t('panel.ariaFinanciado', { pct })}>
            <circle cx="21" cy="21" r="15.9155" fill="none" stroke="#232833" strokeWidth="5" />
            <circle cx="21" cy="21" r="15.9155" fill="none" stroke="#e3a82b" strokeWidth="5" strokeDasharray={`${pct} ${100 - pct}`} strokeDashoffset="25" strokeLinecap="butt" />
            <text x="21" y="22.5" textAnchor="middle" className="p7m-dona-n">{pct}%</text>
            <text x="21" y="27" textAnchor="middle" className="p7m-dona-t">{t('mercado.financiado')}</text>
          </svg>
          <ul className="p7m-leyenda">
            <li><i className="p7m-pt oro" /><div><span>{t('panel.invertido')}</span><strong>{money(num(p.amount_investment))} USDT</strong></div></li>
            <li><i className="p7m-pt gris" /><div><span>{t('panel.disponible')}</span><strong>{money(restante)} USDT</strong></div></li>
            <li><i className="p7m-pt vacio" /><div><span>{t('panel.participantes')}</span><strong>{p.total_inversores ?? '—'}</strong></div></li>
          </ul>
        </section>

        {!secundario && parcelas.length > 1 && (
          <div className="p7m-parcelas">
            <span>{t('panel.parcelas', { n: parcelas.length })}</span>
            <div>
              {parcelas.map((x) => (
                <button type="button" key={x.id} className={x.id === p.id ? 'on' : ''} onClick={() => setP(x)} title={x.name}>
                  {x.nrocontrato || `#${x.id}`} <em>{avance(x)}%</em>
                </button>
              ))}
            </div>
          </div>
        )}

        <dl className="p7m-datos p7m-datos-6">
          <div><dt>{t('panel.tasa')}</dt><dd>{money(p.tax_referential_client)}%</dd></div>
          <div><dt>{t('panel.vence')}</dt><dd>{p.end_date ? String(p.end_date).slice(0, 10) : '—'}</dd></div>
          <div><dt>{t('panel.plazoRestante')}</dt><dd>{textoDias(dias)}</dd></div>
          <div><dt>{t('panel.riesgo')}</dt><dd>{p.risk || '—'}</dd></div>
          <div><dt>{t('panel.garantia')}</dt><dd>{p.warranty || '—'}</dd></div>
        </dl>

        {docs.length > 0 && (
          <section className="p7m-docs" aria-label={t('panel.docsAria')}>
            <h3>{t('panel.docs')}</h3>
            <ul>
              {docs.map((d) => (
                <li key={d.id}>
                  <a href={d.file_path} target="_blank" rel="noopener noreferrer">
                    <strong>{d.name}</strong>
                    <span>{d.type_label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {exito ? (
          <div className="p7m-ok" role="status">
            <strong>{t('panel.listo')}</strong>
            <p>{exito}</p>
            <button type="button" className="p7-btn-ghost" onClick={onCerrar}>{t('panel.cerrar')}</button>
          </div>
        ) : (
          <section className="p7m-op">
            {secundario ? (
              <div className="p7m-fila"><span>{t('panel.precioCompra')}</span><strong>{money(importe)} USDT</strong></div>
            ) : (
              <>
                <label htmlFor="p7m-monto">{t('panel.cuanto')}</label>
                <input id="p7m-monto" type="number" min={MINIMO} step="1" value={monto} onChange={(e) => setMonto(e.target.value)} inputMode="decimal" />
                <div className="p7m-atajos">
                  {ATAJOS.map((a) => <button key={a} type="button" className={num(monto) === a ? 'on' : ''} onClick={() => setMonto(String(a))}>{a}</button>)}
                </div>
              </>
            )}
            <div className="p7m-fila"><span>{t('panel.recibirias')}</span><strong>≈ {money(estimado)} USDT</strong></div>
            <div className="p7m-fila p7m-fila-mute"><span>{t('panel.disponibleWallet')}</span><strong className={falta ? 'p7m-neg' : ''}>{money(disponibleWallet)} USDT</strong></div>
            {error && <div className="p7-wallet-error" role="alert">{error}</div>}
            <button type="button" className="p7-btn-gold p7m-cta" disabled={enviando} onClick={confirmar}>
              {enviando ? t('panel.procesando') : secundario ? t('panel.confirmarCompra') : t('panel.confirmarInversion')}
            </button>
            <p className="p7m-aviso">{t('panel.aviso')}</p>
          </section>
        )}
      </aside>
    </div>
  );
};

export default PanelOperacion;
